import { normalizeQcm } from '../normalizers/normalizeQcm';
import { normalizeVraiFaux } from '../normalizers/normalizeVraiFaux';
import { normalizeSaisie } from '../normalizers/normalizeSaisie';
import PrintEnonce from '../helpers/PrintEnonce';
import { modeEffectif, tableEnListe } from '../helpers/saisiePolicy';
import { isVraiFaux, indexFigurePartagee } from '../helpers/groupQuestions';
import { estTableau, qcmConvertible, tableConvertible } from '../helpers/reponseLibre';
import { itemLourdFigure, besoinGrilleFigure } from '../helpers/besoinFigureGroup';
import { QcmBody } from './PrintQcm';
import { SaisieBody } from './PrintSaisie';
import { VraiFauxBody, TableLibreBody } from './PrintVraiFaux';
import { AssociationBody } from './PrintAssociation';

/**
 * Renderer print d'un bloc à FIGURE PARTAGÉE et types hétérogènes
 * ('figure-group') : un seul header « Exercice N », une consigne de tête
 * générique, la figure rendue UNE seule fois, puis les sous-questions
 * repérées a. b. c. — chacune avec SA consigne (les types diffèrent : on ne
 * peut pas les couvrir d'une consigne unique), son énoncé (figure partagée
 * skippée) et son corps (mêmes corps que les singles).
 *
 * `reponseLibre` (axe format) : conversion décidée ITEM par item — un QCM à
 * choix textuels perd ses choix, un tableau convertible passe en liste, les
 * autres items sont inchangés.
 */
const CONSIGNE_TETE = 'Réponds aux questions à partir de la figure.';
const CONSIGNE_TETE_CAHIER = 'Réponds aux questions sur ton cahier à partir de la figure.';

/** Repère a. b. c. — cohérent avec la feuille V/F libre et le corrigé. */
const repereLettre = (i) => `${String.fromCharCode(97 + (i % 26))}.`;

/** L'item passe-t-il réellement en réponse libre ? */
const itemLibre = (payload, reponseLibre) => {
    if (!reponseLibre) return false;
    if (estTableau(payload)) return tableConvertible(payload);
    if (payload?.type === 'qcm') return qcmConvertible(payload);
    return false;
};

/** Texte d'énoncé de l'item, via le normalizer de son type. */
const texteItem = (payload) => {
    if (estTableau(payload)) return normalizeVraiFaux(payload).question;
    if (payload?.type === 'qcm') return normalizeQcm(payload).question;
    if (payload?.type === 'texte') return normalizeSaisie(payload).question;
    return payload?.texte || '';
};

/** Corps de l'item selon son type — mêmes corps que les renderers single. */
const CorpsItem = ({ payload, saisieMode, libre }) => {
    if (estTableau(payload)) {
        const enCahier = modeEffectif(payload, saisieMode) === 'cahier';
        if (tableEnListe(payload, saisieMode, libre)) {
            return <TableLibreBody payload={payload} enCahier={enCahier} />;
        }
        const { lignes, colonnes } = normalizeVraiFaux(payload);
        return <VraiFauxBody lignes={lignes} colonnes={colonnes} estVF={isVraiFaux(payload)} enCahier={enCahier} />;
    }
    if (payload?.type === 'qcm') {
        const norm = normalizeQcm(payload);
        return <QcmBody payload={payload} norm={norm} saisieMode={saisieMode} libre={libre} />;
    }
    if (payload?.type === 'texte') {
        const { correctAnswer, suffixe } = normalizeSaisie(payload);
        return <SaisieBody correctAnswer={correctAnswer} suffixe={suffixe} saisieMode={saisieMode} />;
    }
    if (payload?.type === 'association') {
        return <AssociationBody payload={payload} saisieMode={saisieMode} />;
    }
    return null;
};

const PrintFigureGroup = ({ exNum, items, figurePartagee, saisieMode = 'sur_la_feuille', reponseLibre = false }) => {
    const enCahier = saisieMode === 'sur_le_cahier';
    // Grille des sous-questions : le CSS place 2 colonnes quand la place
    // réelle le permet (besoin fourni ici), les items lourds occupent leur
    // rangée entière (cf. besoinFigureGroup).
    const besoin = besoinGrilleFigure(items);
    // Une seule consigne, générique, en tête : pas de mini-consigne par
    // sous-question — le corps de chaque item (choix, trous, tableau) suffit
    // à comprendre le geste attendu (décision utilisateur 2026-08-28).
    return (
        <div className="ep-exo ep-figure-group">
            <span className="ep-exo-num">{exNum}</span>
            <p className="ep-exo-consigne">{enCahier ? CONSIGNE_TETE_CAHIER : CONSIGNE_TETE}</p>
            {figurePartagee?.element ? (
                <PrintEnonce
                    className="ep-figgroup-figure"
                    texte=""
                    elements={[figurePartagee.element]}
                />
            ) : null}
            <ol
                className="ep-figgroup-list ep-figgroup-grid"
                style={besoin > 0 ? { '--ep-besoin': `${besoin}px` } : undefined}
            >
                {items.map(({ payload, originalIndex }, idx) => {
                    const skipIdx = indexFigurePartagee(payload, figurePartagee?.cle);
                    const libre = itemLibre(payload, reponseLibre);
                    const lourd = itemLourdFigure(payload);
                    return (
                        <li
                            key={payload?.id ?? `sub-${originalIndex}`}
                            className={`ep-figgroup-item${lourd ? ' ep-figgroup-lourd' : ''}`}
                        >
                            <span className="ep-vf-repere">{repereLettre(idx)}</span>
                            <div className="ep-figgroup-item-corps">
                                <PrintEnonce
                                    className="ep-qcm-group-question"
                                    texte={texteItem(payload)}
                                    elements={payload?.elements_additionnels?.elements}
                                    skipTables={estTableau(payload)}
                                    skipIndices={skipIdx >= 0 ? [skipIdx] : null}
                                />
                                <CorpsItem payload={payload} saisieMode={saisieMode} libre={libre} />
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

export default PrintFigureGroup;
